import { useEffect, useState } from "react";

import api from "api/api";
import endpoint from "api/endpoints";
import { filterPromotionsByCustomerId } from "utils/promotionFilterUtil";

/**
 * Fetches one product by id, on demand.
 *
 * For surfaces that start from a listing row and then need the full record — quick look
 * is the case this exists for. The listing payload is `_getStandardFields()`, which
 * deliberately omits `storedetaileddescription`, `custitem38/39` and the rest; the
 * by-id endpoint returns `_getExtendedFields()`.
 *
 * `enabled` gates the request so nothing is fetched until the surface is actually opened.
 * That matters here: a 48-card grid would otherwise fire 48 requests to populate dialogs
 * nobody opened.
 *
 * The response goes through `filterPromotionsByCustomerId` per CLAUDE.md rule 6 —
 * skipping it leaks restricted promotions to the wrong customer category. `api.get` and
 * `endpoint` per rule 4.
 *
 * @param {string|number} id
 * @param {boolean} enabled Fetch only while true.
 * @returns {{product: object|null, loading: boolean, error: string|null}}
 */
export default function useProductById(id, enabled = true) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !id) return undefined;

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    api
      .get(endpoint.GET_PRODUCT_BY_ID(id), { signal: controller.signal })
      .then((res) => {
        setProduct(filterPromotionsByCustomerId(res.data));
        setLoading(false);
      })
      .catch((err) => {
        // A closed dialog aborts its own request; that isn't a failure to report.
        if (controller.signal.aborted || err?.code === "ERR_CANCELED") return;
        setError(err?.response?.data?.error || "Failed to load product.");
        setLoading(false);
      });

    return () => controller.abort();
  }, [id, enabled]);

  return { product, loading, error };
}
